// Shared streak visuals for the two streak bodies: the daily CalendarSummaryView
// (a 2-D month heatmap) and the weekly/monthly StreakStripView (a 1-D period
// strip). Both light up the CURRENT run (green) and the LONGEST/record run
// (gold) over a neutral logged/missed backdrop, and both top the visual with the
// same Current-vs-Longest banner. Factored out here so the two read identically
// and can't drift — only the cell granularity and the banner's unit word differ.

import React, { useEffect } from 'react'
import { animate, motion, useMotionValue, useTransform } from 'motion/react'
import { SLICE_COLORS } from '../../../charts/bodies/chartColors'
import bearGold from '../../../assets/images/honey_bear_gold.png'
import bearGreen from '../../../assets/images/honey_bear_green.png'
import bearNeutral from '../../../assets/images/honey_bear_neutral.png'
import bearEmpty from '../../../assets/images/honey_bear_empty.png'

// Two well-separated hues from the shared palette: green for the live/current
// run, gold for the all-time record.
export const CURRENT_COLOR = SLICE_COLORS[1] // '#82ca9d'
export const LONGEST_COLOR = SLICE_COLORS[2] // '#ffc658'
// Neutral grays for the binary backdrop: ordinary logged vs no check-in.
export const LOGGED_COLOR = '#d1d5db' // gray-300
export const EMPTY_COLOR = '#e5e7eb' // gray-200
// The "logged" cell color for the logging_rate coverage visual — the green
// reads as "you showed up" against the gray missed backdrop, so the sparse (or
// dense) green pattern communicates the percentage at a glance.
export const COVERAGE_COLOR = SLICE_COLORS[1] // the same green as CURRENT_COLOR

// The honey-bear bottle, in the four states a bucket can be in. These four
// PNGs are the only copies kept — they were cut from one piece of artwork
// showing three bottles side by side, which isn't in the repo, so re-deriving
// them means starting from that source again. What was done to it, for whoever
// does:
//
//   - Sliced on the gaps between the bottles, into one shared box so all four
//     line up, then exported at 4x the strip's height to stay crisp on hi-dpi.
//   - The empty bottle's interior came back filled with the artwork's backdrop
//     grey — a background remover can't tell a hollow inside from the outside —
//     so that colour was keyed out again and the outline/fill boundary pixels
//     un-blended, leaving it genuinely hollow with no grey fringe on white.
//   - `neutral` isn't in the source art at all. It's the green bottle with its
//     green body rescaled onto gray-300, preserving the artwork's own shading
//     so the outlines stay proportionally darker, and the amber cap untouched.
//     A flat luminance ramp was tried first and crushed the body to #c0c0c0,
//     which made a merely-logged bucket compete with the record run.

export type BearState = 'gold' | 'green' | 'neutral' | 'empty'

const BEAR_SRC: Record<BearState, string> = {
  gold: bearGold,
  green: bearGreen,
  neutral: bearNeutral,
  empty: bearEmpty,
}

/** The bottles are all one silhouette in one box, so a single aspect ratio
 *  keeps every cell the same width however tall the strip draws them. */
export const BEAR_ASPECT = 111 / 224

/** One honey-bear bottle, always drawn at full strength. Fading it to mark the
 *  in-progress bucket doesn't work: the artwork's amber cap washes out to beige
 *  and the whole bottle reads as a fourth, muddier asset rather than as the
 *  empty one it actually is. The strip marks that bucket around the bottle
 *  instead — see `pending` in StreakStripView. */
export const HoneyBear: React.FC<{
  state: BearState
  /** Rendered height in px; width follows BEAR_ASPECT. */
  height: number
}> = ({ state, height }) => (
  <img
    src={BEAR_SRC[state]}
    alt=""
    aria-hidden
    draggable={false}
    style={{ height, width: Math.round(height * BEAR_ASPECT) }}
  />
)

/** A number that springs from 0 up to `value` on mount, in step with the
 *  banner's bar. Clamped to `value` so the spring's slight overshoot can't
 *  flash a count above the real streak. */
const CountUp: React.FC<{ value: number; className?: string }> = ({ value, className }) => {
  const mv = useMotionValue(0)
  const text = useTransform(mv, (v) => Math.min(value, Math.round(v)))
  useEffect(() => {
    const controls = animate(mv, value, { type: 'spring', stiffness: 500, damping: 30 })
    return () => controls.stop()
  }, [mv, value])
  return <motion.span className={className}>{text}</motion.span>
}

/** The compare-at-a-glance banner: Current vs Longest, with a thin bar reading
 *  the current run as a fraction of the record (the green fill rising toward the
 *  gold record track — full when current matches it). `unitLabel` is the run's
 *  unit word ('days' for the daily calendar; 'wks' / 'mos' for the cadence
 *  strip). */
export const StreakBanner: React.FC<{
  current: number
  longest: number
  unitLabel?: string
}> = ({ current, longest, unitLabel = 'days' }) => {
  const pct = longest > 0 ? Math.min(100, Math.round((current / longest) * 100)) : 0

  const Dot: React.FC<{ color: string }> = ({ color }) => (
    <span className="inline-block h-2 w-2 rounded-sm" style={{ backgroundColor: color }} />
  )

  return (
    <div className="mb-3 rounded-lg border border-gray-100 bg-gray-50 p-3">
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wide text-gray-400">
            <Dot color={CURRENT_COLOR} /> Current
          </div>
          <div className="flex items-baseline gap-1">
            <CountUp value={current} className="text-2xl font-bold tabular-nums text-gray-800" />
            <span className="text-xs text-gray-400">{unitLabel}</span>
          </div>
        </div>
        <div className="h-8 w-px bg-gray-200" />
        <div className="flex-1 text-right">
          <div className="flex items-center justify-end gap-1.5 text-[10px] font-medium uppercase tracking-wide text-gray-400">
            Longest <Dot color={LONGEST_COLOR} />
          </div>
          <div className="flex items-baseline justify-end gap-1">
            <span className="text-2xl font-bold tabular-nums text-gray-800">{longest}</span>
            <span className="text-xs text-gray-400">{unitLabel}</span>
          </div>
        </div>
      </div>

      {/* Current (green fill) as a fraction of the record (gold track). A runner
          rides the tip of the fill as it grows. */}
      <div className="relative mt-2 h-2">
        <div
          className="h-full overflow-hidden rounded-full"
          style={{ backgroundColor: `${LONGEST_COLOR}55` }}
        >
          <motion.div
            className="h-full rounded-full"
            style={{ backgroundColor: CURRENT_COLOR }}
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
          />
        </div>
        {pct > 0 && (
          <motion.span
            aria-hidden
            className="pointer-events-none absolute top-1/2 text-3xl leading-none"
            style={{ transform: 'translate(-50%, -65%) scaleX(-1)' }}
            initial={{ left: 0 }}
            animate={{ left: `${pct}%` }}
            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
          >
            🏃
          </motion.span>
        )}
      </div>
    </div>
  )
}

/** The logging_rate headline: the % of cadence buckets logged over the period,
 *  with the active/total counts and a bar filling to that share. The percentage
 *  is the backend's single source of truth (passed in), so the banner and the
 *  sibling Stats tab always show the same number. `unitLabel` is the bucket word
 *  ('days' / 'wks' / 'mos'). */
export const CoverageBanner: React.FC<{
  pct: number
  active: number
  total: number
  unitLabel?: string
}> = ({ pct, active, total, unitLabel = 'days' }) => {
  const clamped = Math.max(0, Math.min(100, pct))
  return (
    <div className="mb-3 rounded-lg border border-gray-100 bg-gray-50 p-3">
      <div className="flex items-baseline gap-2">
        <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wide text-gray-400">
          <span className="inline-block h-2 w-2 rounded-sm" style={{ backgroundColor: COVERAGE_COLOR }} />
          Logged
        </div>
        <div className="ml-auto text-xs text-gray-400">
          {active} / {total} {unitLabel}
        </div>
      </div>
      <div className="mt-0.5 flex items-baseline gap-1">
        {/* Rendered exactly (not via the integer CountUp) so the headline matches
            the Stats tab's one-decimal percentage. */}
        <span className="text-2xl font-bold tabular-nums text-gray-800">{pct}</span>
        <span className="text-sm font-bold text-gray-800">%</span>
      </div>

      {/* The share as a filled bar over a neutral track. */}
      <div className="mt-2 h-2 overflow-hidden rounded-full bg-gray-200">
        <motion.div
          className="h-full rounded-full"
          style={{ backgroundColor: COVERAGE_COLOR }}
          initial={{ width: 0 }}
          animate={{ width: `${clamped}%` }}
          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        />
      </div>
    </div>
  )
}
